import { Dialog, DialogContent, DialogTitle, List, ListItem } from '@mui/material';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { isSameDay } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMonthlyToolsQuery } from '../../../../hooks/queries/useToolsQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { MonthlyTool } from '../../../../services/apiSLTP/materials/tools';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { formatDate } from '../../../../utils/dates';
import { crudStatus, getMaterialStateInfos, permissionLevels } from '../../../../utils/enums';
import StateCell from '../../../cells/StateCell';
import Icon from '../../../Icon';
import GenericAgGridTable from '../../GenericAgGridTable';
import { getColorByPercentage, getMaterialStateColor } from '../../../../utils/colors';
import { AgBarSeriesItemStylerParams, AgBarSeriesStyle, AgSparklineOptions } from 'ag-charts-community';
import Holidays from 'date-holidays';
import IconButton from '../../../shared/IconButton';
import { useTheme } from '../../../../hooks/useTheme';
import dayjs from 'dayjs';
const hd = new Holidays('FR');

interface MonthlyToolsTableProps {
    currentDate: Date;
}

const MonthlyToolsTable: React.FC<MonthlyToolsTableProps> = ({ currentDate }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { muiTheme, mode } = useTheme();
    const [selectedTool, setSelectedTool] = useState<MonthlyTool['dailyToolsByDate'][number]['daily'] | null>(null);

    const { data: dataMonthlyTools, isLoading: isMonthlyToolsLoading } = useMonthlyToolsQuery({
        params: { date: currentDate.toISOString() },
    });

    const daysInMonth = useMemo(() => {
        const days = [];
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const numDays = new Date(year, month + 1, 0).getDate();

        for (let day = 1; day <= numDays; day++) {
            const d = new Date(year, month, day);
            days.push({
                date: d,
                label: d.getDate().toString(),
            });
        }
        return days;
    }, [currentDate]);

    const rowData = useMemo(() => {
        if (!dataMonthlyTools) return [];

        return dataMonthlyTools.sort((a, b) => {
            if (a.toolTypeId.designation > b.toolTypeId.designation) return -1;
            if (a.toolTypeId.designation < b.toolTypeId.designation) return 1;
            return a.designation.localeCompare(b.designation);
        });
    }, [dataMonthlyTools, daysInMonth]);

    const itemStyler: (params: AgBarSeriesItemStylerParams<any>) => AgBarSeriesStyle = (params) => {
        const max = Math.max(...rowData.map((value) => value.dailyToolsByDate.length));

        const color = getColorByPercentage(params.yValue ?? 0, max);
        return { fill: color };
    };

    const columnDefs: ColDef[] = useMemo(() => {
        const baseCols: ColDef<MonthlyTool>[] = [
            {
                headerName: capitalizeFirstLetter(t('name')),
                field: 'designation',
                colId: 'name',
                pinned: 'left',
                initialWidth: 300,
                cellStyle: (params) =>
                    params.data?.crudStatus === crudStatus.DELETED
                        ? { color: muiTheme.palette.error.main }
                        : params.data?.crudStatus === crudStatus.INACTIVE
                          ? { color: muiTheme.palette.action.disabled }
                          : undefined,
            },
            {
                headerName: capitalizeFirstLetter(t('type')),
                field: 'toolTypeId.designation',
                colId: 'type',
                pinned: 'left',
                initialWidth: 120,
            },
            {
                headerName: capitalizeFirstLetter(t('codeProC')),
                field: 'codeProC',
                colId: 'codeProC',
                pinned: 'left',
                initialWidth: 120,
            },
            {
                headerName: capitalizeFirstLetter(t('prodDays')),
                colId: 'totalparkline',
                cellRenderer: 'agSparklineCellRenderer',
                pinned: 'left',
                valueGetter: (params) => {
                    return [params.data?.dailyToolsByDate.length];
                },
                comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
                    return valueA - valueB;
                },
                cellRendererParams: {
                    sparklineOptions: {
                        type: 'bar',
                        direction: 'horizontal',
                        min: 0,
                        max: Math.max(...rowData.map((value) => value.dailyToolsByDate.length)) + 5,

                        label: {
                            enabled: true,
                            placement: 'outside-end',
                            color: mode === 'dark' ? '#fff' : '#000',
                        },

                        itemStyler: itemStyler,
                    } as AgSparklineOptions,
                },
            },
        ];

        const dateCols: ColDef<MonthlyTool>[] = daysInMonth.map(({ date, label }, index) => ({
            headerName: label,
            colId: `day_${index + 1}`,
            initialWidth: 120,
            valueGetter: (params) => params.data?.dailyToolsByDate.find((value) => isSameDay(new Date(value.date), date))?.daily.length,
            cellRenderer: (params: ICellRendererParams<MonthlyTool>) => {
                const currentToolDay = params.data?.dailyToolsByDate.find((value) => isSameDay(new Date(value.date), date));
                const toolDate = new Date();
                return currentToolDay ? (
                    <div className="fx-r fx-items-c gap-5px text-grey">
                        <StateCell text={currentToolDay.daily.length.toString()} stateColor={''} showDot={false} />
                        {!!currentToolDay.daily.length && <IconButton iconName="infoSharp" onClick={() => setSelectedTool(currentToolDay.daily)} />}
                    </div>
                ) : (
                    <div className=" text-gray">{t('n/a').toUpperCase()}</div>
                );
            },
            headerStyle: {
                color: hd.isHoliday(date)
                    ? muiTheme.palette.error.main
                    : date.getDay() === 6
                      ? muiTheme.palette.warning.main
                      : date.getDay() === 0
                        ? muiTheme.palette.error.main
                        : '',
            },
            suppressFloatingFilterButton: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
        }));

        const adminCols: ColDef<MonthlyTool>[] =
            user?.profileId.permsLevel === permissionLevels.ADMIN
                ? [
                      {
                          headerName: '_id',
                          field: '_id',
                          colId: '_id',
                          initialWidth: 250,
                          cellClass: 'usage-col-main',
                          headerClass: 'usage-col-main',
                      },
                  ]
                : [];

        return [...baseCols, ...dateCols, ...adminCols];
    }, [t, daysInMonth, user, rowData, mode]);

    return (
        <>
            <GenericAgGridTable
                rowData={rowData}
                columnDefs={columnDefs}
                exportCsvFileName={`presence-tools-${dayjs(currentDate).format('YYYY-MM')}`}
                agGridProps={{ getRowId: ({ data }) => data._id }}
                loading={isMonthlyToolsLoading}
            />
            <Dialog open={!!selectedTool} onClose={() => setSelectedTool(null)}>
                <DialogTitle>{capitalizeFirstLetter(t('sites'))}</DialogTitle>
                <DialogContent>
                    <List>
                        {selectedTool?.length ? (
                            selectedTool?.map((daily, i) => (
                                <ListItem key={i} className="gap-5px fx-r">
                                    <span className="font-w-700">{daily?.site.name}</span>
                                    <span>{'-'}</span>
                                    <span style={{ color: getMaterialStateColor(daily.dailyTool.state) }}>
                                        {capitalizeFirstLetter(t(getMaterialStateInfos(daily.dailyTool.state)))}
                                    </span>
                                    <span>{'-'}</span>
                                    {capitalizeFirstLetter(daily.dailyTool.isUsed ? t('used') : t('notUsed'))}
                                </ListItem>
                            ))
                        ) : (
                            <ListItem>{t('noSite')}</ListItem>
                        )}
                    </List>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MonthlyToolsTable;
